import { isNil, pick, keyBy, isEmpty } from 'mojito/utils';
import MojitoCore from 'mojito/core';
import UserSettingsTypes from 'services/user-settings/types.js';
import BetsTypes from 'services/bets/types.js';
import MarketGroupTypes from 'services/sports-content/market-groups/types.js';
import EventTypes from './types.js';

const { CLOCK_DIRECTION, EVENT_TYPE, MARKET_TYPE_CATEGORY, STARTING_PRICE_ODDS } = EventTypes;
const { DateTimeUtils } = MojitoCore.Base;
const { SP } = BetsTypes.PRICE_TYPE;
const StringUtils = MojitoCore.Base.StringUtils;

const mapMarketsOnLineIds = (markets, marketIds) => {
    const marketsMap = keyBy(markets, ({ id }) => id);
    return marketIds.map(marketId => marketsMap[marketId]);
};

/**
 * Event data model utilities.
 *
 * @class EventUtils
 * @name utils
 * @memberof Mojito.Services.SportsContent.Events
 */
export default class EventUtils {
    /**
     * Checks if event type is Outright.
     *
     * @param {string} eventType - Event type.
     *
     * @returns {boolean} True if it is an outright.
     * @function Mojito.Services.SportsContent.Events.utils.isOutrightEventType
     */
    static isOutrightEventType(eventType) {
        return eventType === EVENT_TYPE.OUTRIGHT;
    }

    /**
     * Checks if events of the specified type are considered as match.
     *
     * @param {string} eventType - Event type.
     *
     * @returns {boolean} True if considered match event.
     * @function Mojito.Services.SportsContent.Events.utils.isMatchEventType
     */
    static isMatchEventType(eventType) {
        return eventType === EVENT_TYPE.MATCH;
    }

    /**
     * Checks if events of the specified type are considered as racing sport event.
     *
     * @param {string} eventType - Event type.
     *
     * @returns {boolean} True if considered race event.
     * @function Mojito.Services.SportsContent.Events.utils.isRaceEventType
     */
    static isRaceEventType(eventType) {
        return eventType === EVENT_TYPE.RACE;
    }

    /**
     * Check if race is abandoned.
     *
     * @param {object} raceEvent - Race event data.
     *
     * @returns {boolean} True if race is abandoned.
     * @function Mojito.Services.SportsContent.Events.utils.isAbandonedRace
     */
    static isAbandonedRace(raceEvent) {
        return raceEvent.details.raceState === EventTypes.RACE_STATE.ABANDONED;
    }

    /**
     * Check if race is forthcoming.
     *
     * @param {object} raceEvent - Race event data.
     *
     * @returns {boolean} True if race is yet to come. False otherwise.
     * @function Mojito.Services.SportsContent.Events.utils.isForthcomingRace
     */
    static isForthcomingRace(raceEvent) {
        const { startTime } = raceEvent;
        const { raceStatus } = raceEvent.details;

        const secondsToStart = EventUtils.getSecondsToStart(startTime);
        const isUpcoming = raceStatus === EventTypes.RACE_STATUS.UPCOMING;

        return isUpcoming && secondsToStart > 0;
    }

    /**
     * Check if the current time + countdownSeconds + minutesToAdd is bigger than start time in seconds.
     *
     * @param {Date} startTime - A date and time string in ISO 8601 format.
     * @param {number} countdownSeconds - Seconds that should be added to the current time.
     * @param {number} minutesToAdd - Minutes that should be added to the current time.
     *
     * @returns {boolean} True if criteria for countdown is met. False otherwise.
     * @function Mojito.Services.SportsContent.Events.utils.isCountdownCriteriaMet
     */
    static isCountdownCriteriaMet(startTime, countdownSeconds, minutesToAdd) {
        const startTimeInSeconds = Math.floor(new Date(startTime).getTime() / 1000);
        const timeNowInSeconds = Math.floor(new Date().getTime() / 1000);
        const criteriaForCountdownInSeconds =
            timeNowInSeconds + Math.floor(countdownSeconds + minutesToAdd * 60);

        return criteriaForCountdownInSeconds > startTimeInSeconds;
    }

    /**
     * Get seconds to start of the race.
     *
     * @param {Date} startTime - A date and time string in ISO 8601 format.
     *
     * @returns {number} Seconds to start of the race.
     * @function Mojito.Services.SportsContent.Events.utils.getSecondsToStart
     */
    static getSecondsToStart(startTime) {
        return Math.floor(DateTimeUtils.diffInSeconds(new Date(startTime), new Date()));
    }

    /**
     * Check if race event has enhanced each way terms.
     *
     * @param {object} raceEvent - Race event data.
     *
     * @returns {boolean} True if race has enhanced each way terms.
     * @function Mojito.Services.SportsContent.Events.utils.isEnhancedEachWayTermsRace
     */
    static isEnhancedEachWayTermsRace(raceEvent) {
        return !!raceEvent.details.enhancedEachWayTerms;
    }

    /**
     * Start timer if condition for it is met.
     *
     * @param {object} raceEvent - Race event data.
     * @param {number} countdownMinutes - Indicates how many minutes should be left to show the timer.
     * @param {Mojito.Services.SportsContent.Events.types.startTimerCallback} callback - Callback to start the timer.
     *
     * @returns {number|void} If time to show timer hasn't come yet returns timeoutID. Void otherwise.
     * @function Mojito.Services.SportsContent.Events.utils.raceEventStartupTimer
     */
    static raceEventStartupTimer(raceEvent, countdownMinutes, callback) {
        const { startTime } = raceEvent;

        const secondsToStart = EventUtils.getSecondsToStart(startTime);
        const countdownSeconds = countdownMinutes * 60;
        const isCountdownCriteriaMet = EventUtils.isCountdownCriteriaMet(
            startTime,
            countdownSeconds,
            30
        );

        if (EventUtils.isForthcomingRace(raceEvent) && isCountdownCriteriaMet) {
            if (secondsToStart > countdownSeconds) {
                const msToSetShowCountdown = (secondsToStart - countdownSeconds) * 1000;

                return setTimeout(() => callback(countdownSeconds), msToSetShowCountdown);
            }

            callback(secondsToStart);
        }
    }

    /**
     * Checks if the status of a market is active.
     *
     * @param {string} marketStatus - Parent market status.
     *
     * @returns {boolean} True if market's status should be considered to be active.
     * @function Mojito.Services.SportsContent.Events.utils.marketIsActive
     */
    static marketIsActive(marketStatus) {
        return marketStatus === EventUtils.STATUS.ACTIVE;
    }

    /**
     * Checks if a selection with the specified status and ancestor statuses
     * is considered suspended.
     * <br>
     * If at least one of the statuses is considered suspended, the selection is considered suspended.
     *
     * @param {string} selectionStatus - Selection status.
     * @param {string} marketStatus - Parent market status.
     * @param {string} eventStatus - Ancestor event status.
     *
     * @returns {boolean} True if selection should be considered to be suspended.
     * @function Mojito.Services.SportsContent.Events.utils.selectionIsSuspended
     */
    static selectionIsSuspended(selectionStatus, marketStatus, eventStatus) {
        return (
            marketStatus === EventUtils.STATUS.SUSPENDED ||
            eventStatus === EventUtils.STATUS.SUSPENDED ||
            selectionStatus === EventUtils.STATUS.SUSPENDED
        );
    }

    /**
     * Checks if the specified prices array has any prices.
     *
     * @param {object[]} prices - Array of prices.
     *
     * @returns {boolean} True if array has at least one price.
     * @function Mojito.Services.SportsContent.Events.utils.pricesAreAvailable
     */
    static pricesAreAvailable(prices) {
        return Boolean(EventUtils.getPrice(prices));
    }

    /**
     * Get available price by price type from prices list.
     * Returns first "non-disabled price" if no priceType provided.
     *
     * @param {object[]} prices - Array of prices.
     * @param {Mojito.Services.Bets.types.PRICE_TYPE} [priceType] - Price type. If not provided then the first available will be used.
     *
     * @returns {object|undefined} The price or undefined if no available price found.
     * @function Mojito.Services.SportsContent.Events.utils.getPrice
     */
    static getPrice(prices, priceType) {
        if (priceType) {
            return prices?.find(price => price.type === priceType && !price.disabled);
        }
        return prices?.find(price => !price.disabled);
    }

    /**
     * Decides which of the odds should be used based on the provided odds format.
     *
     * If the decimal odds is -1 then 'ODDS_UTILS.STARTING_PRICE' translation will be returned instead.
     *
     * @param {object} price - Selection price content object. Typically, retrieved from {code}selection.prices{code} using {@link Mojito.Services.SportsContent.Events.utils.getPrice|getPrice} function.
     * @param {Mojito.Services.UserSettings.types.ODDS_FORMAT} oddsFormat - Odds format value.
     * @param {object} [l10n] - Translations object with strings. If not provided then starting price string (SP) won't be returned for odds that are considered to be SP price type.
     *
     * @returns {string} Returns the odds string according to odds format.
     * @function Mojito.Services.SportsContent.Events.utils.getOdds
     */
    static getOdds(price, oddsFormat, l10n) {
        const { ODDS_FORMAT } = UserSettingsTypes;
        const typesMap = {
            [ODDS_FORMAT.FRACTIONAL]: price?.fractionalLabel,
            [ODDS_FORMAT.DECIMAL]: price?.decimalLabel,
            [ODDS_FORMAT.AMERICAN]: price?.americanLabel,
        };
        const hasStartingPrice =
            parseFloat(price?.decimalLabel) === parseFloat(STARTING_PRICE_ODDS);
        const spLabel =
            hasStartingPrice && l10n
                ? StringUtils.resolveString('$ODDS_UTILS.STARTING_PRICE', l10n)
                : undefined;
        return !isEmpty(spLabel) ? spLabel : typesMap[oddsFormat];
    }

    /**
     * Get odds to display. Odds with {@link Mojito.Services.Bets.types.PRICE_TYPE.LP|LP} price type
     * will be displayed normally, while {@link Mojito.Services.Bets.types.PRICE_TYPE.SP|SP} price type
     * will be replaced with `ODDS_UTILS.STARTING_PRICE` string from l10n object.
     *
     * @param {string} oddsString - Odds.
     * @param {Mojito.Services.Bets.types.PRICE_TYPE} priceType - Price type.
     * @param {object} l10n - Object with strings.
     *
     * @returns {string} Odds or starting price translated string.
     *
     * @function Mojito.Services.SportsContent.Events.utils.getDisplayedOdds
     */
    static getDisplayedOdds(oddsString, priceType, l10n) {
        return (priceType === SP ? EventUtils.getSPLabel(l10n) : oddsString) || '';
    }

    /**
     * Get starting price string.
     * The output will be resulted to localized string from <code>l10n</code> object using `ODDS_UTILS.STARTING_PRICE` key to be resolved.
     *
     * @param {object} l10n - Object with strings.
     *
     * @returns {string|undefined} Translated string if l10n exist, otherwise undefined.
     *
     * @function Mojito.Services.SportsContent.Events.utils.getSPLabel
     */
    static getSPLabel(l10n) {
        return l10n ? StringUtils.resolveString('$ODDS_UTILS.STARTING_PRICE', l10n) : undefined;
    }

    /**
     * Compares selections by price decimalLabel for sorting.
     *
     * @param {object} selection1 - The first selection to compare.
     * @param {object} selection2 - The second selection to compare.
     *
     * @returns {number} Returns -1 if 'selection1' should be sorted before 'selection2', 1 if 'selection2' should be sorted before 'selection1', and 0 if they are equal.
     * @function Mojito.Services.SportsContent.Events.utils.compareByPrice
     */
    static compareByPrice(selection1, selection2) {
        const price1 = EventUtils.getPrice(selection1.prices);
        const price2 = EventUtils.getPrice(selection2.prices);
        if (price1 || price2) {
            if (!price1) {
                return 1;
            }
            if (!price2) {
                return -1;
            }
            return parseFloat(price1.decimalLabel) - parseFloat(price2.decimalLabel);
        }
        return 0;
    }

    /**
     * Compares selections by sort order for sorting.
     *
     * @param {object} selection1 - The first selection to compare.
     * @param {object} selection2 - The second selection to compare.
     *
     * @returns {number} Returns -1 if 'selection1' should be sorted before 'selection2', 1 if 'selection2' should be sorted before 'selection1', and 0 if they are equal.
     * @function Mojito.Services.SportsContent.Events.utils.compareBySortOrder
     */
    static compareBySortOrder(selection1, selection2) {
        return selection1.sortOrder - selection2.sortOrder;
    }

    /**
     * Checks if the specified test price is higher than the reference price.
     * Returns false, if one or more prices are not defined.
     *
     * @param {object} refPrice - Reference price object.
     * @param {object} testPrice - Test price object.
     *
     * @returns {boolean} True if the test price is higher than the reference price, else false.
     * @function Mojito.Services.SportsContent.Events.utils.priceIsHigher
     */
    static priceIsHigher(refPrice, testPrice) {
        if (!refPrice || !testPrice) {
            return false;
        }

        return Number.parseFloat(testPrice.decimalLabel) > Number.parseFloat(refPrice.decimalLabel);
    }

    /**
     * Check if event has three participants.
     *
     * @param {object} event - Event to check for three participants.
     *
     * @function Mojito.Services.SportsContent.Events.utils.isThreeParticipantsEvent
     * @returns {boolean} True if event has three participants, otherwise false.
     */

    static isThreeParticipantsEvent(event) {
        const { participants } = event || {};
        return participants?.length === 3;
    }

    /**
     * Check if an event provides all
     * {@link Mojito.Services.SportsContent.Events.types.IN_PLAY_CLOCK_DETAILS|in play clock details}. The clock data
     * can typically be used to control an in play clock for the event.
     *
     * @param {object} event - Event to check for in play clock data.
     *
     * @returns {boolean} True if the event has all required in play clock data, otherwise false.
     * @function Mojito.Services.SportsContent.Events.utils.hasInPlayClockDetails
     */
    static hasInPlayClockDetails(event) {
        const { details } = event || {};
        const { timeInformation, clockDirection, isClockRunning } = details || {};
        return (
            !isNil(timeInformation) &&
            !isNil(isClockRunning) &&
            !isNil(CLOCK_DIRECTION[clockDirection])
        );
    }

    /**
     * Get time from in play details time information. Adjusts the time according to when it started.
     *
     * @param {{ time: number, validAt: number }} timeInfo - In play details time information.
     * @param {boolean} isClockRunning - Boolean indicating if clock is running. If not running, validAt won't be considered.
     * @param {Mojito.Services.SportsContent.Events.types.CLOCK_DIRECTION} clockDirection - Clock direction.
     *
     * @returns {number} Time.
     */
    static calculateTime(timeInfo, isClockRunning, clockDirection) {
        const { time = 0, validAt } = timeInfo || {};
        const countingDown = clockDirection === CLOCK_DIRECTION.B;
        // validAt will be the seconds from unix time if this is provided.
        let diff = 0;
        if (validAt && isClockRunning) {
            const currentDate = new Date();
            const secondsSinceEpoch = Math.round(currentDate.getTime() / 1000);
            diff = secondsSinceEpoch - validAt;
        }
        const timeWhenCountingDown = time - diff < 0 ? 0 : time - diff;
        const timeWhenCountingUp = time + diff;
        return countingDown ? timeWhenCountingDown : timeWhenCountingUp;
    }

    /**
     * Get time from in play details of live event.
     * Uses {@link Mojito.Services.SportsContent.Events.utils.calculateTime|calculateTime} to resolve the value.
     *
     * @param {object} event - Event content object.
     *
     * @returns {number} Time.
     */
    static getEventInplayTime(event) {
        const { details = {} } = event || {};
        const { timeInformation, isClockRunning, clockDirection } = details;
        return EventUtils.calculateTime(timeInformation, isClockRunning, clockDirection);
    }

    /**
     * Returns odds from selection based on provided <code>oddsFormat</code>.
     *
     * @param {object} selection - Selection content object.
     * @param {Mojito.Services.UserSettings.types.ODDS_FORMAT} oddsFormat - Odds format value.
     * @param {boolean} [isBaseOdds = false] - Should get base selection odds.
     *
     * @returns {string} Odds from selection according to provided <code>oddsFormat</code>.
     * @function Mojito.Services.SportsContent.Events.utils.getSelectionOdds
     */
    static getSelectionOdds(selection, oddsFormat, isBaseOdds = false) {
        const price =
            (isBaseOdds ? selection.basePrice : EventUtils.getPrice(selection.prices)) || {};
        return EventUtils.getOdds(price, oddsFormat);
    }

    /**
     * Finds selection object by id inside <code>market.selections</code>.
     *
     * @param {object} market - Market content object.
     * @param {string} selectionId - Selection id.
     *
     * @returns {object} Selection content object.
     * @function Mojito.Services.SportsContent.Events.utils.getSelectionFromMarket
     */
    static getSelectionFromMarket(market, selectionId) {
        const { selections } = market || {};
        return selections?.find(selection => selection.id === selectionId);
    }

    /**
     * Finds selection objects for each id in selectionIds by iterating through markets list.
     * Uses {@link Mojito.Services.SportsContent.Events.utils.getSelectionFromMarket|getSelectionFromMarket} to resolve selection by id within particular market.
     *
     * @param {Array<object>} markets - Market content object list.
     * @param {Array<string>} selectionIds - Selection ids list.
     *
     * @returns {Array<object>} Selection content objects.
     * @function Mojito.Services.SportsContent.Events.utils.getSelectionsFromMarkets
     */
    static getSelectionsFromMarkets(markets, selectionIds) {
        return selectionIds.flatMap(selectionId => {
            return (markets || [])
                .map(market => EventUtils.getSelectionFromMarket(market, selectionId))
                .filter(Boolean);
        });
    }

    /**
     * Returns the number of accepted prebuilt bets containing a selection, when it reaches the given minimum.
     *
     * @param {object} [selection] - Selection content object.
     * @param {number} minimumBetCount - Minimum number of prebuilt bets required to return the count. If -1 then the count is always returned, including 0.
     *
     * @returns {number|undefined} Prebuilt bet count, or undefined if the selection has no prebuilt bet count or it is below <code>minimumBetCount</code>.
     * @function Mojito.Services.SportsContent.Events.utils.getPrebuiltBetCount
     */
    static getPrebuiltBetCount(selection, minimumBetCount) {
        const prebuiltBetCount = selection?.prebuiltBetCount;
        const isLimited = minimumBetCount !== -1;
        if (
            Number.isFinite(prebuiltBetCount) &&
            (!isLimited || prebuiltBetCount >= minimumBetCount)
        ) {
            return prebuiltBetCount;
        }
    }

    /**
     * Checks if market belongs to 'Home Draw Away' type. Means that it is three-way
     * and can have 1X2 selections set.
     *
     * @param {object} market - Market to check.
     *
     * @returns {boolean} Returns true if market is 'Home Draw Away'.
     * @function Mojito.Services.SportsContent.Events.utils.isHDAMarket
     */
    static isHDAMarket(market) {
        return market.typeCategory === MARKET_TYPE_CATEGORY.TEAM_HDA;
    }

    /**
     * Checks if market belongs either to 'Home Draw Away' or 'Over Exactly Under' type category.
     * Means that it is three-way and can have '1' 'X' '2' or 'O' 'E' 'U' selections set.
     *
     * @param {object} market - Market to check.
     *
     * @returns {boolean} Returns true if market is 'Home Draw Away' or 'Over Exactly Under'.
     * @function Mojito.Services.SportsContent.Events.utils.isThreeWayMarket
     */
    static isThreeWayMarket(market) {
        if (!market) {
            return false;
        }
        const { OVER_EXACTLY_UNDER } = MARKET_TYPE_CATEGORY;
        const { typeCategory } = market;
        return EventUtils.isHDAMarket(market) || typeCategory === OVER_EXACTLY_UNDER;
    }

    /**
     * Creates aggregated markets object. Handy to be used for rendering.
     *
     * @param {Mojito.Services.SportsContent.MarketGroups.types.AggregatedMarketInfo} marketInfo - Aggregated market descriptor object. Defines market lines that should be aggregated and generic market meta info.
     * @param {Array<object>} markets - List of market objects to be aggregated.
     *
     * @returns {Mojito.Services.SportsContent.Events.types.AggregatedMarket|undefined} Aggregated market with the list of markets and additional meta info.
     * @function Mojito.Services.SportsContent.Events.utils.createAggregatedMarket
     */
    static createAggregatedMarket(marketInfo, markets) {
        if (!marketInfo) {
            return;
        }

        const lineIds = marketInfo.markets.map(market => market?.id);
        const mostBalancedLineMarketId = marketInfo.markets.find(
            market => !!market?.mostBalanced
        )?.id;

        return {
            ...pick(marketInfo, 'name', 'displayOrder'),
            key: marketInfo.id,
            type: EventUtils.resolveAggregatedMarketType(marketInfo, markets),
            markets: mapMarketsOnLineIds(markets, lineIds),
            eachWayTerms: markets[0]?.eachWayTerms,
            mask: marketInfo.mask,
            mostBalancedLineMarketId,
        };
    }

    /**
     * Gets markets data by event market lines prop.
     *
     * @param {Array<object>} markets - List of markets objects.
     * @param {Array<object>} events - LIst of events object.
     *
     * @returns {Array<{data: object, id: string, eventId: string}>} List of markets data to update.
     * @function Mojito.Services.SportsContent.Events.utils.getMarketsDataByMarketLine
     */
    static getMarketsDataByMarketLine(markets, events) {
        const result = [];
        markets.forEach(market => {
            const event = events.find(event => {
                const marketLines = Object.values(event.marketLines);
                return marketLines.some(marketLine => market.id === marketLine.id);
            });
            if (event) {
                result.push({ data: market, id: market.id, eventId: event.id });
            }
        });

        return result;
    }

    /**
     * Gets markets data that are present in Promotions.
     *
     * @param {Array<object>} markets - List of markets objects.
     * @param {Array<object>} promotions - LIst of promotions object.
     *
     * @returns {Array<{data: object, id: string, eventId: string}>} List of markets data to update.
     * @function Mojito.Services.SportsContent.Events.utils.getMarketsDataByPromotions
     */
    static getMarketsDataByPromotions(markets, promotions) {
        const result = [];

        // Safety check: ensure promotions exists
        if (!promotions) {
            return result;
        }

        // Handle promotions as either an array or a single object
        const promotionsArray = Array.isArray(promotions) ? promotions : [promotions];

        // Collect all promotion cards from all promotion objects
        const allPromoCards = [];
        promotionsArray.forEach(promotionObj => {
            if (promotionObj?.locations) {
                Object.values(promotionObj.locations)
                    .flat()
                    .forEach(card => allPromoCards.push(card));
            }
        });

        // Deduplicate by id and filter for cards with market info
        const promoList = Object.values(
            allPromoCards.reduce((acc, item) => ({ ...acc, [item.id]: item }), {})
        ).filter(promo => promo?.event?.market?.marketId !== undefined);

        // Extract all market IDs from promotions
        const promoMarketIds = promoList
            .map(promotion => promotion?.event?.market?.marketId)
            .filter(id => id !== undefined);

        // Find matching markets and create result entries
        promoList.forEach(promotion => {
            markets.forEach(market => {
                if (promoMarketIds.includes(market.id)) {
                    result.push({ data: market, id: market.id, eventId: promotion.event.id });
                }
            });
        });

        return result;
    }

    /**
     * Resolves aggregated market type from marketInfo and markets list.
     *
     * @param {Mojito.Services.SportsContent.MarketGroups.types.AggregatedMarketInfo} marketInfo - Aggregated market descriptor object.
     * @param {Array<object>} markets - List of market objects.
     *
     * @returns {Mojito.Services.SportsContent.MarketGroups.types.AGGREGATED_MARKET_TYPE} Aggregated market type.
     * @function Mojito.Services.SportsContent.Events.utils.resolveAggregatedMarketType
     */
    static resolveAggregatedMarketType(marketInfo, markets = []) {
        const { CORRECT_SCORE } = MarketGroupTypes.AGGREGATED_MARKET_TYPE;
        // For most of the aggregated markets types we can rely on marketInfo.type property.
        // But for Correct Score we need to check if markets inside has correctScore flag (we can relay on first market only as all markets inside aggregated market should be of the same type).
        // This is needed because of very weird configuration setup in AdapterProperties. There are two parameters which are conflicting/duplicating each other: mojito.adapter.correct-score-market-types and mojito.adapter.correct-score-market.
        // The first config is used to define marketInfo.type as CORRECT_SCORE. The second one is used to define correctScore flag on market object itself. We need to relay on a second one as it can be specified by sport as well.
        // Ideally mojito.adapter.correct-score-market-types should be removed in favour of mojito.adapter.correct-score-market only. Once this is done we can remove this special handling.
        return markets[0]?.correctScore ? CORRECT_SCORE : marketInfo.type;
    }
}

EventUtils.STATUS = {
    UNKNOWN: 'UNKNOWN',
    ACTIVE: 'ACTIVE',
    SUSPENDED: 'SUSPENDED',
};
